import { useEffect } from 'react'
import { RTKQueryResponse } from '../../typings'
import { ELIGIBILITY_STATES } from '../constants'
import { useGetEligibilityQuery } from '../services/checkEligibility'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import {
  getEligibilityState,
  setIsCheckingEligibility,
} from '../store/slices/eligibilitySlice'
import {
  clearHandledError,
  setHandledError,
} from '../store/slices/handledErrorSlice'
import { setCreateGroupFlow } from '../store/slices/sheetsSlice'
import {
  selectBackgroundImageUrl,
  setBackgroundImageUrl,
} from '../store/slices/groupFormSlice'
import { isCoverOnCreateFlowEnabled } from '../utils/features'
import { HANDLED_ERROR_CODES } from '../utils/errors/errorLookup'

export function useEligibilityCheck(isCreateGroup: boolean) {
  const dispatch = useAppDispatch()
  const eligibilityState = useAppSelector(getEligibilityState)
  const { data, isSuccess, isFetching } = useGetEligibilityQuery(null, {
    skip: !isCreateGroup, // this allows us to load this component, but not execute query until we want it
  })
  const formBackgroundImagedUrl = useAppSelector(selectBackgroundImageUrl)

  const eligibilityData = data?.customer?.moneyPools?.moneyPoolEligibility
  const isEligible = eligibilityData?.decision === ELIGIBILITY_STATES.ELIGIBLE

  const checkEligibility = () => {
    dispatch(clearHandledError())
    dispatch(setIsCheckingEligibility(true))
  }

  const goToCreateFlow = () => {
    // used for turning off the spinning indicator on the Create a Group Button
    dispatch(setIsCheckingEligibility(false))

    // enable the Creation flow
    dispatch(setCreateGroupFlow({ toggleCreateGroupFlow: true }))
    if (isCoverOnCreateFlowEnabled() && !formBackgroundImagedUrl) {
      dispatch(
        setBackgroundImageUrl(
          eligibilityData.configuration?.defaultMediaFile?.file?.referenceUrl,
        ),
      )
    }
  }

  useEffect(() => {
    if (!isFetching && isCreateGroup && eligibilityData && !isEligible) {
      dispatch(setCreateGroupFlow({ toggleCreateGroupFlow: false }))

      const reasons = eligibilityData.ineligibilityReasons.map(
        (reason: { reason: string }) => reason.reason,
      )

      /* The eligibility query does not return an INELIGIBLE response as an API error
       *  This is because it also returns other configuration data.
       *  So, we will construct an error response so that we can use the handledErrorSlice
       *  and the errorLookup mapping/functionality  */
      const pseudoRTKQueryResponse: RTKQueryResponse = {
        data: {
          error: {
            code: reasons[0],
            message: 'Eligibility',
            name: 'Error',
            type: 'api',
            statusCode: 400,
          },
        },
      }

      // allow error code to be found in errorLookup.ts
      dispatch(setHandledError(pseudoRTKQueryResponse)(dispatch))
      dispatch(setIsCheckingEligibility(false))
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }

    if (!isFetching && isCreateGroup && eligibilityData && isEligible) {
      goToCreateFlow()
    }
  }, [data, eligibilityData, isEligible, isSuccess, isFetching])
  // NOTE: if I add `isCreateGroup` to this array, the effect doesn't fire correctly.  I don't know why.

  const checkIfRedirecting = () => {
    return !!eligibilityData?.ineligibilityReasons?.find(
      (reason: { reason: string }) =>
        reason.reason ===
        HANDLED_ERROR_CODES.MONEY_POOL_CREATION_DENIED_FOR_INACTIVE_PRODUCT_FEATURE,
    )
  }

  return {
    isEligible,
    isSuccess,
    checkEligibility,
    eligibilityState,
    isFetching,
    isRedirecting: checkIfRedirecting(),
  }
}
